import { Link } from 'react-router-dom'

import { Avatar, IMAGE_SIZES } from 'images'
import { FollowButton } from 'tournaments'
import { ProgressBar } from 'ui'
import { useFetch } from 'utils'

import type { Tournament } from 'search'
import type { ServerResponse } from 'utils'

interface Tournament {
  id: string
  authorizations: Array<{
    id: string
    level: number
    updated_at: number
  }>
  avatar: {
    id: string
    filemime: string
    filename: string
    processed_at: number
  }
  comet_code?: string
  country_code: string
  discipline: {
    id: string
    expected_players: number
    name: string
    slug: string
    sport: {
      id: string
      avatar: string
      icon: string
      name: string
      slug: string
    }
  }
  following_group: number
  gender: {
    id: string
    name: string
    slug: string
  }
  institution: {
    id: string
    uuid: string
    country_code: string
    name: string
    nickname: string
    slug: string
  }
  is_active: boolean
  is_ended: boolean
  is_filled: boolean
  name: string
  relevance: number
  season: string
  slug: string
  tournament: {
    id: string
    fair_play_initial_points: number
    is_fair_play_active: boolean
    points_for_lose: number
    points_for_tie: number
    points_for_walkover: number
    points_for_win: number
    season_end: number
    season_start: number
    stages: Array<{
      id: string
      groups_total: number
      is_ended: number
      is_filled: number
      is_started: number
      matches_total: number
      name: string
      order: number
      relevance: number
      scoring: string
      slug: string
      teams_total: number
      type: string
    }>
    teams_total: number
    tie_breakers_order: string
  }
}

const User = () => {
  const tournamentsFollowedFetch = useFetch<ServerResponse<Tournament[]>>('user/tournaments?per_page=24', {
    queryKey: ['tournaments', 'followed'],
    staleTime: 60_000 * 60,
  })

  const tournamentsAdminFetch = useFetch<ServerResponse<Tournament[]>>('user/tournaments/admin?per_page=24', {
    queryKey: ['tournaments', 'admin'],
    staleTime: 60_000 * 60,
  })

  return (
    <div className="UserTournaments p-0 375:p-2 mb-14">
      <div className="bg-white border border-greyLight rounded-lg">
        <div className="font-bold p-2 border-b">Torneos que estás siguiendo:</div>
        {tournamentsFollowedFetch.isFetching && <ProgressBar value={100} />}
        <div className="flex flex-wrap gap-2 p-2 *:w-col-1 *:max-w-full">
          {tournamentsFollowedFetch.data?.data?.map((tournament) => {
            const { stages } = tournament.tournament
            const totalStagesEnded = stages.filter((s) => s.is_ended).length
            const totalStagesFilled = stages.filter((s) => s.is_filled).length
            const progress = stages.length > 0 ? (totalStagesEnded / stages.length) * 100 : 0
            const alert = totalStagesFilled <= stages.length / 2

            return (
              <div
                key={tournament.id}
                className="border hover:border-green focus-within:border-green hover:shadow focus-within:shadow rounded"
              >
                <div className="flex gap-1 items-center px-1 pt-1">
                  <Link
                    className="shrink-0 font-bold truncate !no-underline"
                    tabIndex={-1}
                    to={`/sports/tournament/${tournament.id}`}
                  >
                    <Avatar
                      fallbackIconName="trophy"
                      size="4em"
                      src={tournament.avatar}
                      srcImageSize={IMAGE_SIZES.FIT_100_100}
                    />
                  </Link>
                  <div className="grow flex flex-col overflow-hidden">
                    <Link className="font-bold truncate" to={`/sports/tournament/${tournament.id}`}>
                      {tournament.name}
                    </Link>
                    <div className="text-sm truncate text-greyDark pb-1">
                      Temporada <b>{tournament.season}</b>
                    </div>
                    <div className="text-xs truncate text-greyDark">
                      {tournament.discipline.sport.name}, {tournament.discipline.name}, {tournament.gender.name}
                    </div>
                    <b className="text-xs truncate text-greyDark">{tournament.institution.name}</b>
                    <div className="flex items-center gap-2 text-sm">
                      <i className={`flag ${tournament.country_code} !m-0`} />
                      {tournament.is_ended && <span className="text-green">Finalizado</span>}
                      {tournament.is_active ? (
                        <span className="text-blue">Activo</span>
                      ) : (
                        <span className="text-grey">Inactivo</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end items-center gap-2 text-sm">
                  <FollowButton isFollowing tournamentId={tournament.id} />
                </div>
                <ProgressBar
                  alert={alert}
                  ariaLabel="Avance del torneo"
                  className="text-xxs"
                  tabIndex={-1}
                  value={progress}
                />
              </div>
            )
          })}
          {tournamentsFollowedFetch.data?.data?.length === 0 && (
            <div className="p-2 text-greyDarkBlue">No sigues ningún torneo aún.</div>
          )}
        </div>
      </div>
      <div className="bg-white border border-greyLight rounded-lg mt-2">
        <div className="font-bold p-2 border-b border-greyLight">Torneos que estás administrando:</div>
        {tournamentsAdminFetch.isFetching && <ProgressBar value={100} />}
        <div className="flex flex-wrap gap-2 p-2 *:w-col-1 *:max-w-full">
          {tournamentsAdminFetch.data?.data?.map((tournament) => {
            const { stages } = tournament.tournament
            const totalStagesEnded = stages.filter((s) => s.is_ended).length
            const totalStagesFilled = stages.filter((s) => s.is_filled).length
            const progress = stages.length > 0 ? (totalStagesEnded / stages.length) * 100 : 0
            const alert = totalStagesFilled <= stages.length / 2

            return (
              <div
                key={tournament.id}
                className="border hover:border-green focus-within:border-green hover:shadow focus-within:shadow rounded"
              >
                <div className="flex gap-1 items-center px-1 pt-1">
                  <Link
                    className="shrink-0 font-bold truncate !no-underline"
                    tabIndex={-1}
                    to={`/sports/tournament/${tournament.id}`}
                  >
                    <Avatar
                      fallbackIconName="trophy"
                      size="4em"
                      src={tournament.avatar}
                      srcImageSize={IMAGE_SIZES.FIT_100_100}
                    />
                  </Link>
                  <div className="grow flex flex-col overflow-hidden">
                    <Link className="font-bold truncate" to={`/sports/tournament/${tournament.id}`}>
                      {tournament.name}
                    </Link>
                    <div className="text-sm truncate text-greyDark pb-1">
                      Temporada <b>{tournament.season}</b>
                    </div>
                    <div className="text-xs truncate text-greyDark">
                      {tournament.discipline.sport.name}, {tournament.discipline.name}, {tournament.gender.name}
                    </div>
                    <b className="text-xs truncate text-greyDark">{tournament.institution.name}</b>
                    <div className="flex items-center gap-2 text-sm">
                      <i className={`flag ${tournament.country_code} !m-0`} />
                      {tournament.is_ended && <span className="text-green">Finalizado</span>}
                      {tournament.is_active ? (
                        <span className="text-blue">Activo</span>
                      ) : (
                        <span className="text-grey">Inactivo</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end items-center gap-2 text-sm">
                  <FollowButton
                    isFollowing={!!tournamentsFollowedFetch.data?.data?.find(({ id }) => id === tournament.id)}
                    tournamentId={tournament.id}
                  />
                </div>
                <ProgressBar
                  alert={alert}
                  ariaLabel="Avance del torneo"
                  className="text-xxs"
                  tabIndex={-1}
                  value={progress}
                />
              </div>
            )
          })}
          {tournamentsAdminFetch.data?.data?.length === 0 && (
            <div className="p-2 text-greyDarkBlue">No administras ningún torneo aún.</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default User
